/* ==========================================================================
   Flyout Nav behavior
   - Desktop (> mobile breakpoint): hover with intent delays, focus support
   - Mobile/touch: click to open, second click on the link follows it
   - Keyboard: Esc closes, Tab moves naturally, focus opens panels
   ========================================================================== */
(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-flyout-nav]';
  const PARENT_SELECTOR = '[data-flyout-parent]';
  const TRIGGER_SELECTOR = '[data-flyout-trigger]';

  function init(root) {
    if (!root || root.__flyoutInit) return;
    root.__flyoutInit = true;

    const breakpoint = parseInt(
      getComputedStyle(root.querySelector('.flyout-nav') || root)
        .getPropertyValue('--fn-mobile-breakpoint') || '800',
      10
    );

    const isDesktop = () => window.innerWidth > breakpoint;

    const parents = root.querySelectorAll(PARENT_SELECTOR);
    const openTimers = new WeakMap();
    const closeTimers = new WeakMap();

    function clearTimers(parent) {
      if (openTimers.has(parent)) {
        clearTimeout(openTimers.get(parent));
        openTimers.delete(parent);
      }
      if (closeTimers.has(parent)) {
        clearTimeout(closeTimers.get(parent));
        closeTimers.delete(parent);
      }
    }

    function open(parent) {
      clearTimers(parent);
      parent.classList.add('is-open');
      const trigger = parent.querySelector(':scope > ' + TRIGGER_SELECTOR);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function close(parent) {
      clearTimers(parent);
      parent.classList.remove('is-open');
      const trigger = parent.querySelector(':scope > ' + TRIGGER_SELECTOR);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      // Close any open descendants too
      parent.querySelectorAll('.is-open').forEach(function (el) {
        el.classList.remove('is-open');
        const t = el.querySelector(':scope > ' + TRIGGER_SELECTOR);
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    function closeSiblings(parent) {
      const parentList = parent.parentElement;
      if (!parentList) return;
      Array.from(parentList.children).forEach(function (sibling) {
        if (sibling !== parent && sibling.classList && sibling.classList.contains('is-open')) {
          close(sibling);
        }
      });
    }

    function closeAll() {
      root.querySelectorAll('.is-open').forEach(function (el) {
        el.classList.remove('is-open');
        const t = el.querySelector(':scope > ' + TRIGGER_SELECTOR);
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }

    function getDelay(prop, fallback) {
      const val = getComputedStyle(root.querySelector('.flyout-nav') || root)
        .getPropertyValue(prop)
        .trim();
      const n = parseInt(val, 10);
      return Number.isNaN(n) ? fallback : n;
    }

    parents.forEach(function (parent) {
      const trigger = parent.querySelector(':scope > ' + TRIGGER_SELECTOR);
      if (!trigger) return;

      // ----- Desktop hover (with intent delays) -----
      parent.addEventListener('mouseenter', function () {
        if (!isDesktop()) return;
        const delay = getDelay('--fn-hover-delay-open', 80);
        clearTimers(parent);
        openTimers.set(
          parent,
          setTimeout(function () {
            closeSiblings(parent);
            open(parent);
          }, delay)
        );
      });

      parent.addEventListener('mouseleave', function () {
        if (!isDesktop()) return;
        const delay = getDelay('--fn-hover-delay-close', 200);
        clearTimers(parent);
        closeTimers.set(
          parent,
          setTimeout(function () {
            close(parent);
          }, delay)
        );
      });

      // ----- Click (mobile = always toggle; desktop = first tap opens, second follows) -----
      trigger.addEventListener('click', function (e) {
        if (isDesktop()) {
          // On desktop, only intercept if it's a touch device's first tap
          // (i.e. panel not open yet). Otherwise let the link navigate.
          if (!parent.classList.contains('is-open')) {
            // Heuristic: if the user was hovering, panel would be open already.
            // If it's still closed on click, they likely tapped — open it.
            const wasFromTouch = e.pointerType === 'touch' || e.pointerType === '';
            if (wasFromTouch) {
              e.preventDefault();
              closeSiblings(parent);
              open(parent);
            }
          }
          return;
        }

        // Mobile: toggle accordion
        e.preventDefault();
        if (parent.classList.contains('is-open')) {
          close(parent);
        } else {
          // Only close siblings at the same nesting level on mobile too
          closeSiblings(parent);
          open(parent);
        }
      });

      // ----- Keyboard a11y -----
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          if (parent.classList.contains('is-open')) {
            close(parent);
            trigger.focus();
            e.preventDefault();
          }
        }
        if (e.key === 'ArrowDown' && isDesktop()) {
          if (!parent.classList.contains('is-open')) open(parent);
          const firstItem = parent.querySelector('.flyout-nav__panel a');
          if (firstItem) {
            firstItem.focus();
            e.preventDefault();
          }
        }
      });

      // Open on focus-within for keyboard tabbing on desktop
      trigger.addEventListener('focus', function () {
        if (!isDesktop()) return;
        closeSiblings(parent);
        open(parent);
      });
    });

    // Close everything on outside click
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) closeAll();
    });

    // Close on Escape anywhere within the nav
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });

    // Reset state on resize across breakpoint
    let lastIsDesktop = isDesktop();
    window.addEventListener(
      'resize',
      function () {
        const now = isDesktop();
        if (now !== lastIsDesktop) {
          closeAll();
          lastIsDesktop = now;
        }
      },
      { passive: true }
    );

    // Close flyout when search modal opens
    const searchModal = document.querySelector('details-modal.header__search');
    if (searchModal) {
      // Dawn dispatches this on the details element when opening
      searchModal.addEventListener('click', function (e) {
        const summary = e.target.closest('summary.modal__toggle');
        if (summary) closeAll();
      });
    }

    // Hide flyout nav when search is open
    const searchDetails = document.querySelector('.header__search details');
    if (searchDetails) {
      new MutationObserver(function () {
        if (searchDetails.hasAttribute('open')) {
          root.classList.add('search-is-open');
        } else {
          root.classList.remove('search-is-open');
        }
      }).observe(searchDetails, { attributes: true, attributeFilter: ['open'] });
    }
  }

  function bootstrap() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Re-init after Shopify theme editor section reloads
  document.addEventListener('shopify:section:load', bootstrap);
  document.addEventListener('shopify:section:reorder', bootstrap);
})();